import { Component, Input, OnInit } from "@angular/core";
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { AppointmentsService } from "../appointments.service";
import { LanguageService } from "../language.service";
import { LoginService } from "../login.service";
import { LeadFormDialogComponent } from "../lead-form-dialog/lead-form-dialog.component";

const STAGE_KEYS = {
  "without reason": "Without Reason",
  "No appointments available": "No Appointments Available",
  "After the price was displayed": "After Price Displayed",
  "User exited from booking without appointment": "Exited Booking",
};

const TYPE_KEYS = {
  manual: "Manual",
  message: "Message",
  waitingList: "Waiting List",
  abandoned: "Abandoned",
};

@Component({
  selector: "app-leads",
  templateUrl: "./leads.component.html",
  styleUrls: ["./leads.component.scss"],
  standalone: false,
})
export class LeadsComponent implements OnInit {
  @Input() mode: "leads" | "messages" = "leads";

  leads = [];
  loading = false;
  filters = {
    from: new Date() as Date | null,
    to: new Date() as Date | null,
    status: "pending",
    source: "all",
    leadType: "all",
  };
  pageDirection = "ltr";
  birthByLead: { [id: number]: Date | null } = {};
  idByLead: { [id: number]: string } = {};
  creatingPatientLeadId: number | null = null;
  editingNoteLeadId: number | null = null;
  noteDraft = "";

  constructor(
    public appointmentService: AppointmentsService,
    public languageService: LanguageService,
    public loginService: LoginService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.pageDirection = this.languageService.getPageDirection();
    this.load();
  }

  async load() {
    const params: { [key: string]: string } = {
      status: this.filters.status,
      source: this.filters.source,
      leadType: this.filters.leadType,
    };
    if (this.mode === "messages") params.leadType = "message";
    if (this.mode === "leads") params.excludeType = "message";
    if (this.filters.from) params.from = this.datePipe.transform(this.filters.from, "yyyy-MM-dd");
    if (this.filters.to) params.to = this.datePipe.transform(this.filters.to, "yyyy-MM-dd");
    this.loading = true;
    try {
      const result = await this.appointmentService.getLeads(params);
      this.leads = Array.isArray(result) ? result : [];
    } finally {
      this.loading = false;
    }
  }

  clearFilters() {
    this.filters = { from: null, to: null, status: "all", source: "all", leadType: "all" };
    this.load();
  }

  openNewLead() {
    this.dialog
      .open(LeadFormDialogComponent, { width: "min(46em, 95vw)", autoFocus: false })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.load();
      });
  }

  typeKey(type: string) {
    return "appointments.gui.labels.leads.Type " + (TYPE_KEYS[type] || "Manual");
  }

  createdByText(lead) {
    if (lead.Source !== "manual") return this.translate.instant("appointments.gui.labels.leads.Via Self Booking");
    return lead.createdByName || "";
  }

  stageKey(stage: string) {
    return STAGE_KEYS[stage] ? "appointments.gui.labels.leads.stage." + STAGE_KEYS[stage] : null;
  }

  toggleHandled(lead) {
    this.appointmentService.setLeadHandled(lead.AutoIndex, !lead.Handled).subscribe(() => this.load());
  }

  startEditNote(lead) {
    this.editingNoteLeadId = lead.AutoIndex;
    this.noteDraft = lead.Note || "";
  }

  cancelEditNote() {
    this.editingNoteLeadId = null;
  }

  saveNote(lead) {
    this.appointmentService.updateLeadNote(lead.AutoIndex, this.noteDraft).subscribe(() => {
      lead.Note = this.noteDraft;
      this.editingNoteLeadId = null;
    });
  }

  goToPatient(lead) {
    this.router.navigate(["/appointments/patient", lead.existingPID]);
  }

  startCreatePatient(lead) {
    if (lead.Birth && lead.ID) {
      this.createPatient(lead, new Date(lead.Birth), null);
      return;
    }
    this.creatingPatientLeadId = lead.AutoIndex;
    this.birthByLead[lead.AutoIndex] = lead.Birth ? new Date(lead.Birth) : null;
    this.idByLead[lead.AutoIndex] = "";
  }

  canConfirmPatient(lead) {
    return !!this.birthByLead[lead.AutoIndex] && !!(lead.ID || (this.idByLead[lead.AutoIndex] || "").trim());
  }

  confirmCreatePatient(lead) {
    if (!this.canConfirmPatient(lead)) return;
    this.createPatient(lead, this.birthByLead[lead.AutoIndex], lead.ID ? null : this.idByLead[lead.AutoIndex].trim());
  }

  createPatient(lead, birth: Date, id: string | null) {
    this.appointmentService
      .createPatientFromLead(lead.AutoIndex, this.datePipe.transform(birth, "yyyy-MM-dd"), id)
      .subscribe(() => {
        this.creatingPatientLeadId = null;
        this.load();
      });
  }

  deleteLead(lead) {
    this.appointmentService.deleteLead(lead.AutoIndex).subscribe(() => this.load());
  }

  private t(key: string) {
    return this.translate.instant(key);
  }

  private stageText(stage: string) {
    const key = this.stageKey(stage);
    return key ? this.t(key) : stage;
  }

  exportExcel() {
    const headers = {
      name: this.t("appointments.gui.labels.patient name"),
      mobile: this.t("appointments.gui.labels.Mobile"),
      email: this.t("appointments.gui.labels.Email"),
      id: this.t("billing.gui.placeholders.ID Number"),
      created: this.t("billing.gui.labels.Creation Date"),
      createdBy: this.t("appointments.gui.labels.leads.Created By"),
      type: this.t("appointments.gui.labels.leads.Type"),
      stage: this.t("appointments.gui.labels.leads.Abandon Stage"),
      message: this.t("appointments.gui.labels.message"),
      note: this.t("appointments.gui.labels.leads.Note"),
      payer: this.t("appointments.gui.labels.Payer"),
      exam: this.t("billing.gui.labels.Exam Name"),
      handled: this.t("appointments.gui.labels.leads.Handled"),
    };
    const yes = this.t("appointments.gui.labels.leads.Yes");
    const no = this.t("appointments.gui.labels.leads.No");
    const data = this.leads.map((l, i) => ({
      "#": i + 1,
      [headers.name]: `${l.First || ""} ${l.Last || ""}`,
      [headers.mobile]: l.Mobile,
      [headers.email]: l.Email,
      [headers.id]: l.ID,
      [headers.created]: this.datePipe.transform(l.creationTime, "dd.MM.yyyy HH:mm"),
      [headers.createdBy]: this.createdByText(l),
      [headers.type]: this.t(this.typeKey(l.LeadType)),
      [headers.stage]: this.stageText(l.AbandonStage),
      [headers.message]: l.Message,
      [headers.note]: l.Note,
      [headers.payer]: l.HMO ? this.t("appointments.gui.labels." + l.HMO) : "",
      [headers.exam]: l.ExamType,
      [headers.handled]: l.Handled ? yes : no,
    }));
    const title =
      this.mode === "messages"
        ? this.t("appointments.gui.labels.messages set by patients")
        : this.t("appointments.gui.labels.leads.Leads");
    const sheetName = title.replace(/[\\/?*\[\]:]/g, " ").substring(0, 31);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), sheetName);
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${sheetName}_${new Date().getTime()}.xlsx`
    );
  }
}
